"use client";
import {
  Box,
  Card,
  CardContent,
  Typography,
  styled,
  Paper,
  Link,
  Divider,
  Container,
  Chip,
  Button,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
  Fade,
  Zoom,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  Snackbar,
  Alert,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuIcon from "@mui/icons-material/Menu";
import React, { useRef, useCallback, useState, useEffect } from "react";
import Editor from "@monaco-editor/react";
import Copyright from "../theme/Copyright";
import { useRouter } from "next/navigation";
import { SX_FORMAT, SX_THEME, hoverMessages } from "src/vscode";
import { motion } from "framer-motion";

// Constants for section IDs to use throughout the component
const SECTIONS = {
  OPCODES: "opcodes",
  COMMENTS: "comments",
  VARIABLES: "variables",
  BIGINTS: "bigints",
  HEX_DATA: "hex-data",
  STRINGS: "strings",
  ANNOTATIONS: "annotations",
  REPEAT_BLOCKS: "repeat-blocks",
  FUNCTIONS: "functions",
  IMPORT: "import",
  BREAK: "break",
  ADVANCED: "advanced-features",
  AUTO_SLICE: "auto-slice",
  PUSH_CODE_DATA: "push-code-data",
  CONTEXT_VERIFICATION: "context-verification",
  COPY_TOP_ALT: "copy-top-alt",
  SPLIT_CTX: "split-ctx",
  MOD2: "mod2",
  TO_LEU: "to-leu",
  RESOURCES: "resources",
};

// Styled components with enhanced styling
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "1.5rem",
  marginBottom: "2.5rem",
  position: "relative",
}));

// Animated container for fade-in sections
const AnimatedSection = styled(motion.div)({
  width: "100%",
});

const LogoContainer = styled(motion.div)(({ theme }) => ({
  width: "100%",
  "& img": {
    width: "100%",
    maxHeight: "10vh",
    objectFit: "contain",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },
}));

const EditorContainer = styled(Box)(({ theme }) => ({
  marginY: "12px",
  border: `1px solid ${theme.palette.mode === "dark" ? "#30363d" : "#e0e0e0"}`,
  borderRadius: "6px",
  overflow: "hidden",
  height: "auto",
  boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.1)}`,
  // transition: "all 0.3s ease",
  position: "relative",
  "&:hover": {
    boxShadow: `0 6px 12px ${alpha(theme.palette.common.black, 0.15)}`,
    //transform: "translateY(-2px)",
  },
}));

const SectionCard = styled(Card)(({ theme }) => ({
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: `0 6px 16px ${alpha(theme.palette.common.black, 0.08)}`,
  marginBottom: theme.spacing(4),
  background:
    theme.palette.mode === "dark"
      ? "linear-gradient(135deg, #2d2d33, #383842)"
      : "linear-gradient(135deg, #ffffff, #f8f9fa)",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`,
  },
}));

// Updated SectionTitle with full-width underline and animation
const SectionTitle = styled(Typography)(({ theme }) => ({
  position: "relative",
  display: "inline-block",
  fontWeight: "bold",
  marginBottom: theme.spacing(2),
  "&:after": {
    content: '""',
    position: "absolute",
    bottom: -8,
    left: 0,
    width: "100%",
    height: "3px",
    backgroundColor: theme.palette.mode === "dark" ? "#F4AA31" : "#e65100",
    borderRadius: "2px",
    transition: "width 0.3s ease",
  },
  "&:hover:after": {
    width: "105%",
  },
}));

// Navigation sidebar item
const NavItem = styled(ListItem)(({ theme, isactive }) => ({
  borderLeft: `3px solid ${
    isactive
      ? theme.palette.mode === "dark"
        ? "#F4AA31"
        : "#e65100"
      : "transparent"
  }`,
  backgroundColor: isactive
    ? theme.palette.mode === "dark"
      ? alpha("#F4AA31", 0.1)
      : alpha("#e65100", 0.05)
    : "transparent",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha("#F4AA31", 0.15)
        : alpha("#e65100", 0.1),
    borderLeft: `3px solid ${
      theme.palette.mode === "dark" ? "#F4AA31" : "#e65100"
    }`,
  },
}));

// Usage in LanguageSpecViewer component
// Replace the current NavItem usage with this
const NavItemWrapper = ({ onClick, isactive, children }) => {
  return (
    <NavItem 
      onClick={onClick} 
      isactive={isactive} 
      component="div" 
      sx={{ 
        cursor: 'pointer',
        // You can add additional styling here if needed
      }}
    >
      {children}
    </NavItem>
  );
};
// Updated FeatureChip with gray colors
const FeatureChip = ({ label }) => {
  const theme = useTheme();

  // Create a URL-friendly ID from the label
  const sectionId = label.toLowerCase().replace(/\s+/g, "-");

  // Handle click to scroll to the section
  const handleChipClick = () => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Chip
      label={label}
      onClick={handleChipClick}
      sx={{
        marginRight: theme.spacing(1),
        marginBottom: theme.spacing(1),
        backgroundColor:
          theme.palette.mode === "dark"
            ? alpha("#9e9e9e", 0.2)
            : alpha("#757575", 0.1),
        color: theme.palette.mode === "dark" ? "#e0e0e0" : "#424242",
        borderRadius: "16px",
        cursor: "pointer",
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? alpha("#9e9e9e", 0.3)
              : alpha("#757575", 0.2),
        },
      }}
    />
  );
};

const BackButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#F4AA31" : "#e65100",
  color: "#fff",
  padding: "10px 20px",
  borderRadius: "28px",
  boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.2)}`,
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: theme.palette.mode === "dark" ? "#fb8c00" : "#ef6c00",
    transform: "translateY(-3px)",
    boxShadow: `0 6px 12px ${alpha(theme.palette.common.black, 0.3)}`,
  },
  "&:active": {
    transform: "translateY(-1px)",
  },
}));


// Improved editor options to enhance hover behavior
const editorOptions = {
  readOnly: true,
  minimap: { enabled: false },
  scrollBeyondLastLine: false,
  automaticLayout: true,
  wordWrap: 'on', // Enable word wrapping
  wrappingIndent: 'same', // Maintain the same indentation on wrapped lines
  scrollbar: {
    vertical: "hidden",
    horizontal: "auto",
    horizontalSliderSize: 6,
  },
  lineNumbers: "off",
  folding: false,
  lineDecorationsWidth: 0,
  lineNumbersMinChars: 0,
  renderLineHighlight: "none",
  overviewRulerBorder: false,
  overviewRulerLanes: 0,
  hideCursorInOverviewRuler: true,
  fontFamily: "'Fira Code', 'Droid Sans Mono', 'monospace'",
  fontSize: 14,
  padding: { top: 16, bottom: 16 },
  hover: {
    delay: 200, // Reduced delay for more responsive hover
    sticky: true,
    above: false,
    enabled: true // Explicitly enable hover
  },
  fixedOverflowWidgets: true,
  contextmenu: false,
  colorDecorators: true,
};

// Component for code blocks with enhanced styling
const CodeEditorBlock = ({
  code,
  editorWillMount,
  language = "sx",
  height = "auto",
}) => {
  const theme = useTheme();
  // Determine proper height based on content
  const lineCount = (code.match(/\n/g) || []).length + 1;
  const calculatedHeight = lineCount * 22 + 32; // 22px per line + 32px padding

  return (
    <EditorContainer
      sx={{ height: height === "auto" ? `${calculatedHeight}px` : height }}
    >
      <Editor
        beforeMount={editorWillMount}
        height="100%"
        language={language}
        defaultLanguage={language}
        defaultValue={code}
        value={code}
        options={editorOptions}
        theme="sxTheme"
        width="100%"
        loading={
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              backgroundColor: theme.palette.background.paper,
            }}
          >
            Loading editor...
          </Box>
        }
      />
    </EditorContainer>
  );
};

const LanguageSpecViewer = () => {
  const router = useRouter();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [activeSection, setActiveSection] = useState(SECTIONS.OPCODES);
  const sectionRefs = useRef({});

  // Create refs for all sections
  Object.values(SECTIONS).forEach((section) => {
    sectionRefs.current[section] =
      sectionRefs.current[section] || React.createRef();
  });

  // Track scroll position to update active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;

      // Show/hide scroll indicator
      if (scrollPosition > 300) {
        setShowScrollIndicator(true);
      } else {
        setShowScrollIndicator(false);
      }

      // Determine active section based on scroll position
      const sectionPositions = Object.entries(sectionRefs.current).map(
        ([id, ref]) => {
          const element = document.getElementById(id);
          if (!element) return { id, position: 0 };
          return {
            id,
            position: element.offsetTop - 100, // Offset to trigger slightly before reaching section
          };
        }
      );

      // Sort by position (top to bottom)
      sectionPositions.sort((a, b) => a.position - b.position);

      // Find the last section that the user has scrolled past
      let currentSection = sectionPositions[0]?.id || SECTIONS.OPCODES;
      for (const section of sectionPositions) {
        if (scrollPosition >= section.position) {
          currentSection = section.id;
        } else {
          break;
        }
      }

      setActiveSection(currentSection);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle scroll to section
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
      setDrawerOpen(false);
    }
  };

  const handleGoBack = () => {
    if (router) router.push("/"); // Navigate to home
  };

  // 3. Improved hover provider implementation
  const createHoverProvider = (projectFunctions) => {
    const providerCache = new Map();
    return {
      provideHover: (model, position) => {
        if (!model?.getWordAtPosition) return null;
        const word = model.getWordAtPosition(position);
        if (!word?.word) return null;

        const cacheKey = word.word.toUpperCase();
        if (providerCache.has(cacheKey)) {
          return providerCache.get(cacheKey);
        }

        let result = {
          contents: [
            {
              isTrusted: true,
              supportHtml: true,
              value: "",
              label: "hover content",
            },
          ],
          range: {
            startLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endLineNumber: position.lineNumber,
            endColumn: word.endColumn,
          },
        };

        const formattedWord = `OP_${cacheKey}`;
        const hoverMessage = hoverMessages?.find(
          (m) => m?.value === formattedWord
        );

        if (hoverMessage?.opcodeNum != null) {
          result.contents[0].value = `<div style="max-width: 380px; padding: 5px;">
          <strong>${hoverMessage.value}:0x${hoverMessage.opcodeNum.toString(
            16
          )}:${hoverMessage.opcodeNum}</strong>
          <div style="margin-top: 5px;">${hoverMessage.description}</div>
        </div>`;
        } else {
          const projectFunction = projectFunctions?.find(
            (f) => f?.name?.toUpperCase() === cacheKey
          );
          if (projectFunction?.name) {
            result.contents[0].value = `<div style="max-width: 380px; padding: 5px;">
            <strong>${projectFunction.name}</strong>
            <div style="margin-top: 5px;">${
              projectFunction.description || "Custom project function"
            }</div>
          </div>`;
          }
        }

        providerCache.set(cacheKey, result);
        return result;
      },
    };
  };

  // 4. Updated editorWillMount with global registration check
  const editorWillMount = useCallback((monaco) => {
    if (!monaco) return;

    try {
      // Register languages only once
      if (!monaco.languages.getLanguages().some((lang) => lang.id === "sx")) {
        monaco.languages.register({ id: "sx" });
      }

      if (!monaco.languages.getLanguages().some((lang) => lang.id === "json")) {
        monaco.languages.register({ id: "json" });
      }

      // Define theme
      monaco.editor.defineTheme("sxTheme", SX_THEME);

      // Set syntax highlighting
      monaco.languages.setMonarchTokensProvider(
        "sx",
        SX_FORMAT([
          "signCtx",
          "checkCtx",
          "verifyCtx",
          "copyTopAlt",
          "splitCtx",
          "mod2",
          "toLEU",
          "exampleMacro",
          "autoSlice",
          "character",
        ])
      );

      // // Register hover provider only once using a module-level variable
      // if (!window.__sxHoverProviderRegistered) {
      //   monaco.languages.registerHoverProvider("sx", createHoverProvider());

      //   // Set the flag in the window object to ensure it persists between renders
      //   window.__sxHoverProviderRegistered = true;
      // }
    } catch (error) {
      console.error("Error in editorWillMount:", error);
    }
  }, []);

  // Animation variants for sections
  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Side Navigation */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        variant="temporary"
        sx={{
          width: drawerOpen ? 240 : 0,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: 240,
            boxSizing: "border-box",
            backgroundColor:
              theme.palette.mode === "dark" ? "#1a1a24" : "#ffffff",
            boxShadow:
              theme.palette.mode === "dark"
                ? "1px 0 8px rgba(0,0,0,0.3)"
                : "1px 0 8px rgba(0,0,0,0.1)",
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${
              theme.palette.mode === "dark"
                ? alpha("#fff", 0.1)
                : alpha("#000", 0.1)
            }`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            SX Documentation
          </Typography>
          <IconButton onClick={() => setDrawerOpen(false)}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
        <List sx={{ pt: 1 }}>
          <ListItem sx={{ pb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Basics
            </Typography>
          </ListItem>
          <NavItemWrapper
            onClick={() => scrollToSection(SECTIONS.OPCODES)}
            isactive={activeSection === SECTIONS.OPCODES}
            component="div"
          >
            <ListItemText primary="Opcodes" />
          </NavItemWrapper>
          <NavItemWrapper
            onClick={() => scrollToSection(SECTIONS.COMMENTS)}
            isactive={activeSection === SECTIONS.COMMENTS}
            component="div"
          >
            <ListItemText primary="Comments" />
          </NavItemWrapper>
          <NavItemWrapper
            onClick={() => scrollToSection(SECTIONS.VARIABLES)}
            isactive={activeSection === SECTIONS.VARIABLES}
            component="div"
          >
            <ListItemText primary="Variables" />
          </NavItemWrapper>
          <NavItemWrapper
            onClick={() => scrollToSection(SECTIONS.BIGINTS)}
            isactive={activeSection === SECTIONS.BIGINTS}
            component="div"
          >
            <ListItemText primary="Bigints" />
          </NavItemWrapper>
          <NavItemWrapper
            onClick={() => scrollToSection(SECTIONS.HEX_DATA)}
            isactive={activeSection === SECTIONS.HEX_DATA}
            component="div"
          >
            <ListItemText primary="Hex Data" />
          </NavItemWrapper>
          <NavItemWrapper
            onClick={() => scrollToSection(SECTIONS.STRINGS)}
            isactive={activeSection === SECTIONS.STRINGS}
            component="div"
          >
            <ListItemText primary="Strings" />
          </NavItemWrapper>
          <NavItemWrapper
            onClick={() => scrollToSection(SECTIONS.ANNOTATIONS)}
            isactive={activeSection === SECTIONS.ANNOTATIONS}
            component="div"
          >
            <ListItemText primary="Annotations" />
          </NavItemWrapper>

          <ListItem sx={{ pt: 2, pb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Control Flow
            </Typography>
          </ListItem>
          <NavItemWrapper
            onClick={() => scrollToSection(SECTIONS.REPEAT_BLOCKS)}
            isactive={activeSection === SECTIONS.REPEAT_BLOCKS}
            component="div"
          >
            <ListItemText primary="Repeat Blocks" />
          </NavItemWrapper>
          <NavItemWrapper
            onClick={() => scrollToSection(SECTIONS.FUNCTIONS)}
            isactive={activeSection === SECTIONS.FUNCTIONS}
            component="div"
          >
            <ListItemText primary="Functions/Macros" />
          </NavItemWrapper>
          <NavItemWrapper
            onClick={() => scrollToSection(SECTIONS.IMPORT)}
            isactive={activeSection === SECTIONS.IMPORT}
            component="div"
          >
            <ListItemText primary="Import" />
          </NavItemWrapper>
          <NavItemWrapper
            onClick={() => scrollToSection(SECTIONS.BREAK)}
            isactive={activeSection === SECTIONS.BREAK}
            component="div"
          >
            <ListItemText primary="Break" />
          </NavItemWrapper>

          <ListItem sx={{ pt: 2, pb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Advanced Features
            </Typography>
          </ListItem>
          <NavItemWrapper
            onClick={() => scrollToSection(SECTIONS.ADVANCED)}
            isactive={activeSection === SECTIONS.ADVANCED}
            component="div"
          >
            <ListItemText primary="Overview" />
          </NavItemWrapper>
          <NavItemWrapper
            onClick={() => scrollToSection(SECTIONS.AUTO_SLICE)}
            isactive={activeSection === SECTIONS.AUTO_SLICE}
            component="div"
          >
            <ListItemText primary="AutoSlice" />
          </NavItemWrapper>
          <NavItemWrapper
            onClick={() => scrollToSection(SECTIONS.PUSH_CODE_DATA)}
            isactive={activeSection === SECTIONS.PUSH_CODE_DATA}
            component="div"
          >
            <ListItemText primary="PushCodeData" />
          </NavItemWrapper>
          <NavItemWrapper
            onClick={() => scrollToSection(SECTIONS.CONTEXT_VERIFICATION)}
            isactive={activeSection === SECTIONS.CONTEXT_VERIFICATION}
            component="div"
          >
            <ListItemText primary="Context Verification" />
          </NavItemWrapper>
        </List>
      </Drawer>

      {/* Scroll indicator */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          zIndex: 1100,
          opacity: showScrollIndicator ? 1 : 0,
          transition: "opacity 0.3s ease",
        }}
      >
        <Box
          sx={{
            height: "100%",
            backgroundColor:
              theme.palette.mode === "dark" ? "#F4AA31" : "#e65100",
            width: `${
              (document.documentElement.scrollTop /
                (document.documentElement.scrollHeight -
                  document.documentElement.clientHeight)) *
              100
            }%`,
            transition: "width 0.1s ease",
          }}
        />
      </Box>
      <Container maxWidth="lg" sx={{ flex: "1 0 auto", py: 4 }}>
        <HeaderContainer>
          <LogoContainer>
            <img src="/logo.png" alt="Bitcoin-SX Logo" />
          </LogoContainer>
        </HeaderContainer>

        <SectionCard component={motion.div} whileHover={{ y: -5 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                fontWeight: 600,
                mb: 3,
              }}
              component={motion.h3}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              SX Language Specification
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: "1.1rem", lineHeight: 1.6, mb: 4 }}
              component={motion.p}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              Bitcoin Script is a dual-stack based reverse polish notation
              programming language composed of a set of extended assembly code
              (ASM) operations which direct computer processing units (CPUs) to
              validate transactions. All general purpose programming languages
              (like C, C++, Javascript et al) are eventually transformed into
              low-level ASM to be processed. Script-Extended (SX) builds upon
              Bitcoin Script by adding developer-friendly features that make
              writing, debugging, and maintaining Bitcoin applications
              significantly easier. SX provides a more expressive syntax,
              powerful abstractions, and productivity enhancements while
              maintaining full compatibility with Bitcoin's execution
              environment.
            </Typography>
            <Box
              sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 4 }}
              component={motion.div}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <FeatureChip label="Opcodes" sectionId={SECTIONS.OPCODES} />
              <FeatureChip label="Comments" sectionId={SECTIONS.COMMENTS} />
              <FeatureChip label="Variables" sectionId={SECTIONS.VARIABLES} />
              <FeatureChip label="Bigints" sectionId={SECTIONS.BIGINTS} />
              <FeatureChip label="Hex Data" sectionId={SECTIONS.HEX_DATA} />
              <FeatureChip label="Strings" sectionId={SECTIONS.STRINGS} />
              <FeatureChip
                label="Annotations"
                sectionId={SECTIONS.ANNOTATIONS}
              />
              <FeatureChip
                label="Repeat Blocks"
                sectionId={SECTIONS.REPEAT_BLOCKS}
              />
              <FeatureChip label="Functions" sectionId={SECTIONS.FUNCTIONS} />
              <FeatureChip
                label="Advanced Features"
                sectionId={SECTIONS.ADVANCED}
              />
            </Box>

            <AnimatedSection
              id={SECTIONS.OPCODES}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" id="opcodes">
                Opcodes
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 2 }}>
                SX uses a simple camelCase convention providing direct access to
                every{" "}
                <Link
                  href="https://wiki.bitcoinsv.io/index.php/Opcodes_used_in_Bitcoin_Script"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    color:
                      theme.palette.mode === "dark" ? "#ff9800" : "#e65100",
                    textDecoration: "none",
                    borderBottom: `1px solid ${alpha(
                      theme.palette.mode === "dark" ? "#ff9800" : "#e65100",
                      0.3
                    )}`,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color:
                        theme.palette.mode === "dark" ? "#fb8c00" : "#ef6c00",
                      borderBottomColor:
                        theme.palette.mode === "dark" ? "#fb8c00" : "#ef6c00",
                    },
                  }}
                >
                  opcode
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                </Link>
                . Dropping the `OP_` prefix the language allows the programmer
                draft their software quickly and neatly:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code="true false notIf 2dup endIf tuck bin2num equalVerify 0notEqual hash256 cat // etc..."
              />
            </AnimatedSection>

            <AnimatedSection
              id={SECTIONS.COMMENTS}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" sx={{ mt: 5 }} id="comments">
                Comments
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Support for standard C/C++ and even D like comments:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code={`// Single line comments
/* & nested
/* in/multi-line */
comments */`}
              />
            </AnimatedSection>

            <AnimatedSection
              id={SECTIONS.VARIABLES}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" sx={{ mt: 5 }} id="variables">
                Arguments/Variables
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Inline `.argument` (the following is a variable named 'argument'
                denoted by a prepended period '.') dictate named locations in a
                script where dynamic values are to be inserted:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code=".argument /* Furthermore, IDE simulation attempts to infer types from variable names e.g: */ .sig .pubKey"
              />
            </AnimatedSection>

            <AnimatedSection
              id={SECTIONS.BIGINTS}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" sx={{ mt: 5 }} id="bigints">
                Bigints
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 2 }}>
                SX utilises Javascript like bigint syntax for numbers e.g.
                `-123n`. This makes the most sense given the unbounded nature of
                the Bitcoin protocol's integer system:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code="-123n // Will transform into the corresponding integer"
              />
            </AnimatedSection>

            <AnimatedSection
              id={SECTIONS.HEX_DATA}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" sx={{ mt: 5 }} id="hex-data">
                Hex Data
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Furthermore, much like the bitcoind ASM syntax, data can be
                introduced inline:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code={`0xdeadbeef // hex values
babecafe // with or without '0x'`}
              />
              <Typography variant="body1" sx={{ mb: 3 }}>
                Which is a simple way to push data onto the script/program
                stack.
              </Typography>
            </AnimatedSection>

            <AnimatedSection
              id={SECTIONS.STRINGS}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" sx={{ mt: 5 }} id="strings">
                Strings
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Likewise:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code={`"Double quoted" /* and */ 'single quoted'`}
              />

              <Typography variant="body1" sx={{ mb: 3 }}>
                strings can be used and are transformed by the compiler.
              </Typography>
            </AnimatedSection>

            <AnimatedSection
              id={SECTIONS.ANNOTATIONS}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" sx={{ mt: 5 }} id="annotations">
                Annotations
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Currently limited to the following three types:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code={`@label:"Taggable stack values for debugging"
@l:noQuotesNeeded // Shorthand for @label
.data @test:0xbabecafe // Indicates default simulation script value
@t:-123n // Shorthand for @test
.sig @cs:1n // Used to specify OP_CODESEPARATOR index for signature validation`}
              />

              <Typography variant="body1" sx={{ mb: 3 }}>
                These are a crucial feature for development speed and usability,
                one cannot debug unknown variables for example so the
                `@label:'annotations'` are used often.
              </Typography>
            </AnimatedSection>

            <AnimatedSection
              id={SECTIONS.REPEAT_BLOCKS}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" sx={{ mt: 5 }} id="repeat-blocks">
                Repeat Blocks
              </SectionTitle>

              <Typography variant="body1" sx={{ mb: 2 }}>
                Beginning with the keyword `repeat` followed by a bigint (or
                dynamic .argument) count, repeat blocks must be completed with
                the `end` keyword:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code={`/* Want to reverse the endian of a hash? */
2ef9122d13e2d7803b6ebda48d0d6d1d9e061e92043d6d4cc81e3e82540c1cef
repeat 31n true split end
// Then recombining...
repeat 31n swap cat end`}
              />
            </AnimatedSection>

            <AnimatedSection
              id={SECTIONS.FUNCTIONS}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" sx={{ mt: 5 }} id="functions">
                Functions/Macros
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Initiated with a single:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code={`#character // 'Functions/macros'
  nop 1n drop // For example
end
// Can then be called by their name following the '#' e.g:
character`}
              />

              <Typography variant="body1" sx={{ mb: 1 }}>
                Function/Macro blocks, like repeat blocks, are also completed
                with the `end` keyword. Their simplistic definition and single
                word calling mechanism mirrors that of Python.
              </Typography>
            </AnimatedSection>

            <AnimatedSection
              id={SECTIONS.IMPORT}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" sx={{ mt: 5 }} id="import">
                Import
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Pre-compiled libraries like SX's own std.sxLib or locally
                defined library files can be imported to allow one use their
                defined macros or functions as follows:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code={`import 'std.sxLib' // JS-like syntax`}
              />
            </AnimatedSection>

            <AnimatedSection
              id={SECTIONS.BREAK}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" sx={{ mt: 5 }} id="break">
                Break
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 3 }}>
                The keyword break saves the user from having to write `false
                verify` which is useful for debugging:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code={`break // Expands to \`false verify\``}
              />
            </AnimatedSection>

            <Divider
              sx={{
                my: 4,
                height: "0px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? alpha("#ff9800", 0.2)
                    : alpha("#e65100", 0.1),
              }}
            />

            <AnimatedSection
              id={SECTIONS.ADVANCED_FEATURES}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <Typography
                variant="h4"
                sx={{
                  mb: 4,
                  fontWeight: 600,
                  position: "relative",
                  "&:after": {
                    content: '""',
                    position: "absolute",
                    bottom: -12,
                    left: 0,
                    width: "100%",
                    height: 4,
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#ff9800" : "#e65100",
                    borderRadius: 2,
                  },
                }}
                id="advanced-features"
              >
                Advanced Features
              </Typography>

              <Typography
                variant="body1"
                sx={{ fontSize: "1.1rem", lineHeight: 1.6, mb: 4 }}
              >
                SX goes beyond basic syntax improvements by offering
                sophisticated capabilities for transaction creation. The
                following specialized functions and macros are built-in to SX or
                available through it's standard library, providing high-level
                abstractions for common transaction patterns while maintaining
                granular control over the execution environment:
              </Typography>
              {/* 
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 4 }}>
              <FeatureChip label="PushCodeData" />
              <FeatureChip label="Transaction Signing" />
              <FeatureChip label="Context Verification" />
              <FeatureChip label="Stack Operations" />
              <FeatureChip label="Data Conversion" />
            </Box> */}
            </AnimatedSection>
            <AnimatedSection
              id={SECTIONS.AUTO_SLICE}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" id="auto-slice">
                AutoSlice
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Given the scriptCode section of a script's context, the
                `autoSlice` keyword will automatically split the scriptCode into
                it's constituent chunks of logic & data parts. AutoSlice will
                only work provided the scriptCode argument lengths can be
                inferred from their variable names e.g. '.alicePubKey' === 20
                bytes. This is useful for script replication, enforcing scripts
                to be immutable, and more.{" "}
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code={`.ctx | splitCtx 5n roll /*bring ctx.scriptCode to top of stack*/ autoSlice // Split ctx and slice scriptCode `}
              />
            </AnimatedSection>

            <AnimatedSection
              id={SECTIONS.PUSH_CODE_DATA}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" sx={{ mt: 5 }} id="push-code-data">
                PushCodeData
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 2 }}>
                SX introduces a keyword `pushCodeData` which must be followed by
                a defined macro code block name, and it will then inject it's
                code into the script as a pushData. This is useful for
                apres-PushCTX operations like building outputs or ancestral
                scripts:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code={`pushCodeData exampleMacro `}
              />
            </AnimatedSection>

            <AnimatedSection
              id={SECTIONS.CONTEXT_VERIFICATION}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle
                variant="h5"
                sx={{ mt: 5 }}
                id="context-verification"
              >
                Standard library UTXO Context Verification ++
              </SectionTitle>

              <Typography variant="body1" sx={{ mb: 3 }}>
                The standard library provides essential functions for
                transaction script context validation and more:
              </Typography>

              <SectionTitle variant="h5" sx={{ mt: 3 }}>
                signCtx
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Generates a signature context for transaction signing. Requires
                context bytes and a sigHashType byte e.g. SIGHASH_ALL (41) to be
                on the stack:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code={`// Any-S 'PushTx' Script Context Validation courtesy of Brendan Lee
#signCtx
    toAltStack hash256
    repeat 31n true split end
    repeat 31n swap cat end
    00 cat bin2num 0n 1f num2bin 1n cat add 
    414136d08c5ed2bf3ba048afe6dcaebafeffffffffffffffffffffffffffffff00 @label:curveOrder
    tuck 2n div over lessThan
    if
        over mod over 2n div over lessThan
        if sub else
        nip endif
        else nip
    endif
    repeat 31n dup 0notEqual split end
    repeat 31n swap cat end
    size swap cat
    022079be667ef9dcbbac55a06295ce870b07029bfcdb2dce28d959f2815b16f8179802 @label:derHeader
    swap cat size swap cat 30 @label:sigLen
    swap cat
    fromAltStack // retrieve sighashType
    cat 02b405d7f0322a89d0f9f3a98e6f938fdc1c969a8d1382a2bf66a71ae74a1e83b0
end`}
              />
            </AnimatedSection>

            <AnimatedSection
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" sx={{ mt: 5 }}>
                checkCtx
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Checks a signature against a transaction context:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code={`#checkCtx
  signCtx checkSig
end`}
              />
            </AnimatedSection>

            <AnimatedSection
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" sx={{ mt: 5 }}>
                verifyCtx
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Enforces script context validation appending the verify
                operation to checkCtx:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code={`#verifyCtx
  checkCtx verify
end`}
              />
            </AnimatedSection>

            <AnimatedSection
              id={SECTIONS.COPY_TOP_ALT}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" sx={{ mt: 5 }}>
                copyTopAlt
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Duplicates the top item on the alt stack without destroying it:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code={`#copyTopAlt
  fromAltStack dup toAltStack
end`}
              />
            </AnimatedSection>

            <AnimatedSection
              id={SECTIONS.SPLIT_CTX}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" sx={{ mt: 5 }}>
                splitCtx
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Splits a transaction context into its components for analysis:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code={`#splitCtx
  4n split @l:ctx.txVersion
  32n split @l:ctx.hashPrevouts
  32n split @l:ctx.hashSequence
  36n split @l:ctx.outpoint
  // Plus additional splits affording inspection of the script context for analysis
end`}
              />
            </AnimatedSection>

            <AnimatedSection
              id={SECTIONS.MOD2}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" sx={{ mt: 5 }}>
                mod2
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Performs modulo 2 operation, useful for checking odd/even:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code={`#mod2
  2n mod
end`}
              />
            </AnimatedSection>

            <AnimatedSection
              id={SECTIONS.TO_LEU}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={sectionVariants}
            >
              <SectionTitle variant="h5" sx={{ mt: 5 }}>
                toLEU
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Converts a number to little-endian unsigned format:
              </Typography>
              <CodeEditorBlock
                editorWillMount={editorWillMount}
                code={`#toLEU
  add num2bin size 1sub split drop
end`}
              />
            </AnimatedSection>
          </CardContent>
        </SectionCard>
        <SectionCard
          id={SECTIONS.RESOURCES}
          component={motion.div}
          whileHover={{ y: -3 }}
          sx={{ mt: 4 }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ fontWeight: 600, mb: 3 }}
            >
              Resources
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 3,
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 8px 24px ${alpha(
                      theme.palette.common.black,
                      0.15
                    )}`,
                  },
                }}
              >
                <Typography variant="h6" gutterBottom>
                  IDE Documentation
                </Typography>
                <Typography variant="body2" paragraph>
                  Access comprehensive documentation, tutorials, and examples to
                  master Bitcoin programming.
                </Typography>
                <Button
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                  sx={{
                    borderColor:
                      theme.palette.mode === "dark" ? "#F4AA31" : "#e65100",
                    color:
                      theme.palette.mode === "dark" ? "#F4AA31" : "#e65100",
                    "&:hover": {
                      borderColor:
                        theme.palette.mode === "dark" ? "#fb8c00" : "#ef6c00",
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? alpha("#F4AA31", 0.1)
                          : alpha("#e65100", 0.05),
                    },
                  }}
                >
                  View IDE Docs
                </Button>
              </Paper>

              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 8px 24px ${alpha(
                      theme.palette.common.black,
                      0.15
                    )}`,
                  },
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Community
                </Typography>
                <Typography variant="body2" paragraph>
                  Join the SX developer community to ask questions, share
                  projects, and collaborate.
                </Typography>
                <Button
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                  sx={{
                    borderColor:
                      theme.palette.mode === "dark" ? "#F4AA31" : "#e65100",
                    color:
                      theme.palette.mode === "dark" ? "#F4AA31" : "#e65100",
                    "&:hover": {
                      borderColor:
                        theme.palette.mode === "dark" ? "#fb8c00" : "#ef6c00",
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? alpha("#F4AA31", 0.1)
                          : alpha("#e65100", 0.05),
                    },
                  }}
                >
                  Join Community
                </Button>
              </Paper>
            </Box>
          </CardContent>
        </SectionCard>

        {/* Back button with improved styling */}
        <Box
          sx={{ textAlign: "center", marginTop: "30px", marginBottom: "20px" }}
        >
          <BackButton
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
          >
            Back to IDE
          </BackButton>
        </Box>
        {/* Scrolling progress indicator */}
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "3px",
            zIndex: 1000,
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette.mode === "dark" ? "#F4AA31" : "#e65100",
              width: "0%",
              height: "100%",
              transition: "width 0.1s",
            }}
            className="scroll-progress"
          />
        </Box>

        {/* Floating action buttons */}
        <Box
          sx={{
            position: "fixed",
            bottom: 20,
            right: 20,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            zIndex: 900,
          }}
        >
          <Tooltip title="Toggle Table of Contents" placement="left">
            <Zoom in={true}>
              <IconButton
                aria-label="toggle table of contents"
                onClick={() => setDrawerOpen(!drawerOpen)}
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#F4AA31" : "#e65100",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#fb8c00" : "#ef6c00",
                  },
                  boxShadow: `0 2px 10px ${alpha(
                    theme.palette.common.black,
                    0.2
                  )}`,
                }}
              >
                <MenuIcon />
              </IconButton>
            </Zoom>
          </Tooltip>

          <Tooltip title="Back to Top" placement="left">
            <Zoom in={showScrollIndicator}>
              <IconButton
                aria-label="back to top"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? alpha("#F4AA31", 0.8)
                      : alpha("#e65100", 0.8),
                  color: "#fff",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#fb8c00" : "#ef6c00",
                  },
                  boxShadow: `0 2px 10px ${alpha(
                    theme.palette.common.black,
                    0.2
                  )}`,
                }}
              >
                <ArrowBackIcon sx={{ transform: "rotate(90deg)" }} />
              </IconButton>
            </Zoom>
          </Tooltip>
        </Box>
      </Container>
      <Box
        sx={{
          mt: "auto",
          py: 3,
          backgroundColor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.paper, 0.6)
              : alpha(theme.palette.grey[100], 0.8),
        }}
      >
        <Copyright />
      </Box>
    </div>
  );
};

export default LanguageSpecViewer;
